import { FormattedMessage, useIntl } from 'react-intl'
import FormatCurrency from 'components/Common/FormatCurrency'
import HelpPane from 'components/Common/HelpPane'
import Hr from 'components/Common/Hr'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { LUXURY_PLUS } from 'luxPlus/constants/base'

interface Props {
  offer?: App.SubscriptionOffer
  type?: 'base' | 'comprehensive'
}

function LuxPlusSubscriptionCancellationModal(props: Props) {
  const { offer, type = 'base' } = props
  const intl = useIntl()

  return <Modal title={intl.formatMessage({
    id: 'QJuprflBCoj0lSZc3xm_',
    defaultMessage: '{programName} cancellation policy',
    description: 'Modal title for LuxPlus+ subscription cancellation policy',
  }, { programName: LUXURY_PLUS.PROGRAM_NAME })}
  >
    {type === 'base' && <>
      <BodyText variant="medium">
        {intl.formatMessage({
          id: 'eWqDbz9THJ0YBrNGiqOH',
          defaultMessage: 'If {programName} doesn’t meet your expectations you can cancel your auto-renewal at any time via your account. All member benefits will still be accessible up until the end of your membership anniversary date.',
          description: 'Base cancellation policy text explaining auto-renewal cancellation and benefit access',
        }, { programName: LUXURY_PLUS.PROGRAM_NAME })}
      </BodyText>
      {!!offer?.price && <>
        <br />
        <BodyText variant="medium">
          <FormattedMessage
            id="c63iftrgwslUPDqFCLod"
            defaultMessage="The {annualFee} annual fee is non-refundable."
            description="Non-refundable annual fee notice for LuxPlus+ subscription cancellation"
            values={{
              annualFee: <FormatCurrency format="roundedDollar" value={offer.price} />,
            }}
          />
        </BodyText>
      </>}
    </>}
    {type === 'comprehensive' &&
      <VerticalSpacer gap={20}>
        <BodyText variant="medium">
          {intl.formatMessage({
            id: 'B2ifzL0Z_6nFpnQsgK3F',
            defaultMessage: 'Enjoy peace of mind knowing that the following cancellation options are available to you. You can manage your {programName} membership via your account.',
            description: 'Introductory text for comprehensive cancellation policy options',
          }, { programName: LUXURY_PLUS.PROGRAM_NAME })}
        </BodyText>
        <Group direction="horizontal" verticalAlign="center" gap={8} horizontalAlign="stretch">
          <BodyText variant="medium" weight="bold" wrap="no-wrap">
            {intl.formatMessage({
              id: 'm8zt0ZvLWhmR-tu5NxVH',
              defaultMessage: 'Within {days} days of joining',
              description: 'Label for refund eligibility period after joining LuxPlus+',
            }, { days: LUXURY_PLUS.REFUND_ELIGIBILITY_PERIOD_IN_DAYS })}
          </BodyText>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: 'w8FyIUYbNFmCXOHKSukf',
              defaultMessage: '100% refund provided you haven’t used your membership.',
              description: 'Refund policy text for early cancellation within eligibility period',
            })}
          </BodyText>
        </Group>
        <Hr size="md" />
        <Group direction="horizontal" verticalAlign="center" gap={8} horizontalAlign="stretch">
          <BodyText variant="medium" weight="bold" wrap="no-wrap">
            {intl.formatMessage({
              id: 'CidzUqAxJfPQIn7FbbiR',
              defaultMessage: '{days}+ days after joining',
              description: 'Label for period after refund eligibility has passed',
            }, { days: LUXURY_PLUS.REFUND_ELIGIBILITY_PERIOD_IN_DAYS })}
          </BodyText>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: 'FMPXWkYvpwW4CzidSqD1',
              defaultMessage: 'Non-refundable. You can cancel your annual renewal at anytime, you will have access to all your member benefits until the membership renewal date.',
              description: 'Non-refundable cancellation policy text after eligibility period',
            })}
          </BodyText>
        </Group>
      </VerticalSpacer>}

    <HelpPane />
  </Modal>
}

export default LuxPlusSubscriptionCancellationModal
